import { useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useSupabaseMutation } from "@/hooks/useSupabaseMutation";
import { useRealtimeSubscription } from "@/hooks/useRealtimeSubscription";
import { useAuth } from "@/hooks/useAuth";
import {
  computeActionProgress,
  canCloseForActions,
  type ActionProgress,
} from "@/lib/defectAutomation";

/**
 * TES-1743 — "Massnahme" als eigene Entitaet (defect_action, generalisiert aus defect_qs_subtask).
 *
 * Ein Mangel hat 0..n Massnahmen. Jede Massnahme: Titel, optional Beschreibung/Faelligkeit/Bearbeiter,
 * Status offen->erledigt, optional aus einer Vorlage instanziiert (template_origin_id) und optional mit
 * eigenem PT-TSK-Task verknuepft (defect_task_links.action_id, ueber die Edge Function `defect-task-link`).
 *
 * Alles user-JWT-scoped -> RLS (team-scoped via Parent-Defect, Stand 312) ist die Grenze; nie service_role.
 * Das Abschluss-Gate (canClose) spiegelt public.defect_has_open_actions; die harte Status-Durchsetzung
 * lebt im Workflow-/Pruefungs-Gate (TES-1737).
 */

export type DefectActionStatus = "open" | "done";

export interface DefectActionItem {
  id: string;
  defect_id: string;
  title: string;
  description: string | null;
  type: string;
  status: string;
  assigned_to: string | null;
  due_date: string | null;
  template_origin_id: string | null;
  created_at: string;
}

export interface ActionTaskLink {
  id: string;
  action_id: string;
  pt_task_id: string;
  pt_task_status: string | null;
}

const ACTION_COLS =
  "id, defect_id, title, description, type, status, assigned_to, due_date, template_origin_id, created_at";

const defectActionKeys = {
  all: ["defect-actions"] as const,
  forDefect: (defectId: string) => [...defectActionKeys.all, defectId] as const,
  links: (defectId: string) => ["defect-action-links", defectId] as const,
};

async function fetchActions(defectId: string): Promise<DefectActionItem[]> {
  const { data, error } = await supabase
    .from("defect_action")
    .select(ACTION_COLS)
    .eq("defect_id", defectId)
    .order("created_at", { ascending: true });
  if (error) throw error;
  return (data as DefectActionItem[] | null) ?? [];
}

async function fetchActionLinks(defectId: string): Promise<ActionTaskLink[]> {
  const { data, error } = await supabase
    .from("defect_task_links")
    .select("id, action_id, pt_task_id, pt_task_status")
    .eq("defect_id", defectId)
    .not("action_id", "is", null)
    .order("created_at", { ascending: true });
  if (error) throw error;
  return (data as ActionTaskLink[] | null) ?? [];
}

export interface CreateActionInput {
  title: string;
  description?: string | null;
  dueDate?: string | null;
  assignedTo?: string | null;
}

// fallow-ignore-next-line complexity
async function createAction(defectId: string, input: CreateActionInput): Promise<void> {
  const { error } = await supabase.from("defect_action").insert({
    defect_id: defectId,
    title: input.title,
    description: input.description ?? null,
    due_date: input.dueDate ?? null,
    assigned_to: input.assignedTo ?? null,
    type: "massnahme",
    status: "open",
  });
  if (error) throw error;
}

export interface SetActionStatusInput {
  actionId: string;
  status: DefectActionStatus;
}

async function setActionStatus(input: SetActionStatusInput): Promise<void> {
  const { error } = await supabase
    .from("defect_action")
    .update({
      status: input.status,
      done_at: input.status === "done" ? new Date().toISOString() : null,
    })
    .eq("id", input.actionId);
  if (error) throw error;
}

async function deleteAction(actionId: string): Promise<void> {
  const { error } = await supabase.from("defect_action").delete().eq("id", actionId);
  if (error) throw error;
}

async function applyRules(defectId: string): Promise<number> {
  const { data, error } = await supabase.rpc("apply_action_rules", { _defect_id: defectId });
  if (error) throw error;
  return (data as number | null) ?? 0;
}

export interface LinkActionTaskInput {
  actionId: string;
  ptTaskId: string;
}

interface LinkResponse {
  link_id?: string;
  status_locked?: boolean;
  error?: string;
}

async function linkActionTask(defectId: string, input: LinkActionTaskInput): Promise<void> {
  const { data, error } = await supabase.functions.invoke("defect-task-link", {
    body: { defect_id: defectId, pt_task_id: input.ptTaskId, action_id: input.actionId },
  });
  if (error) throw error;
  if (!data) throw new Error("empty response");
  if ((data as LinkResponse).error) throw new Error((data as LinkResponse).error);
}

async function unlinkActionTask(linkId: string): Promise<void> {
  const { error } = await supabase.from("defect_task_links").delete().eq("id", linkId);
  if (error) throw error;
}

export interface UseDefectActionsResult {
  actions: DefectActionItem[];
  /** PT-TSK-Links je Massnahme (actionId -> Links). */
  linksByAction: Record<string, ActionTaskLink[]>;
  progress: ActionProgress;
  /** Massnahmen-seitig abschliessbar (keine offene Massnahme mehr). */
  canClose: boolean;
  isLoading: boolean;
  error: Error | null;
  addAction: (input: CreateActionInput) => void;
  setActionStatus: (input: SetActionStatusInput) => void;
  removeAction: (actionId: string) => void;
  applyRules: () => void;
  linkActionTask: (input: LinkActionTaskInput) => void;
  unlinkActionTask: (linkId: string) => void;
  isMutating: boolean;
}

// fallow-ignore-next-line complexity
export function useDefectActions(defectId: string): UseDefectActionsResult {
  const { session } = useAuth();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // TES-2032: Live-Nachzug statt pure-pull. Der PT-TSK-Webhook schreibt pt_task_status server-seitig
  // (fremder Write) — ohne Push blieb das gemountete Panel bis zum staleTime-Ablauf/Focus veraltet.
  // Publikation: Mig 409. Kanal-DELETE deckt entfernte Massnahmen/Links (master-data-Kanal).
  useRealtimeSubscription(
    ["defect_action", "defect_task_links"],
    () => {
      if (!defectId) return;
      void queryClient.invalidateQueries({ queryKey: defectActionKeys.forDefect(defectId) });
      void queryClient.invalidateQueries({ queryKey: defectActionKeys.links(defectId) });
    },
    { enabled: !!defectId && !!session },
  );

  const actionsQuery = useQuery({
    queryKey: defectActionKeys.forDefect(defectId),
    queryFn: () => fetchActions(defectId),
    enabled: !!defectId && !!session,
    staleTime: 30_000,
  });

  const linksQuery = useQuery({
    queryKey: defectActionKeys.links(defectId),
    queryFn: () => fetchActionLinks(defectId),
    enabled: !!defectId && !!session,
    staleTime: 30_000,
  });

  const actions = useMemo(() => actionsQuery.data ?? [], [actionsQuery.data]);
  const progress = useMemo(() => computeActionProgress(actions), [actions]);
  const canClose = useMemo(() => canCloseForActions(actions), [actions]);
  const linksByAction = useMemo(() => {
    const map: Record<string, ActionTaskLink[]> = {};
    for (const link of linksQuery.data ?? []) {
      (map[link.action_id] ??= []).push(link);
    }
    return map;
  }, [linksQuery.data]);

  const addMutation = useSupabaseMutation<void, CreateActionInput>({
    mutationFn: (input) => createAction(defectId, input),
    invalidateKeys: defectActionKeys.forDefect(defectId),
    successMessage: t("defects.measures.toasts.addSuccess"),
    errorMessage: t("defects.measures.toasts.addError"),
    showErrorDetails: true,
    toastType: "sonner",
  });

  const statusMutation = useSupabaseMutation<void, SetActionStatusInput>({
    mutationFn: setActionStatus,
    invalidateKeys: defectActionKeys.forDefect(defectId),
    successMessage: t("defects.measures.toasts.statusSuccess"),
    errorMessage: t("defects.measures.toasts.statusError"),
    showErrorDetails: true,
    toastType: "sonner",
  });

  const removeMutation = useSupabaseMutation<void, string>({
    mutationFn: deleteAction,
    invalidateKeys: defectActionKeys.forDefect(defectId),
    successMessage: t("defects.measures.toasts.removeSuccess"),
    errorMessage: t("defects.measures.toasts.removeError"),
    showErrorDetails: true,
    toastType: "sonner",
  });

  const applyRulesMutation = useSupabaseMutation<number, void>({
    mutationFn: () => applyRules(defectId),
    invalidateKeys: defectActionKeys.forDefect(defectId),
    successMessage: t("defects.measures.toasts.applyRulesSuccess"),
    errorMessage: t("defects.measures.toasts.applyRulesError"),
    showErrorDetails: true,
    toastType: "sonner",
  });

  const linkMutation = useSupabaseMutation<void, LinkActionTaskInput>({
    mutationFn: (input) => linkActionTask(defectId, input),
    invalidateKeys: defectActionKeys.links(defectId),
    successMessage: t("defects.measures.toasts.linkSuccess"),
    errorMessage: t("defects.measures.toasts.linkError"),
    showErrorDetails: true,
    toastType: "sonner",
  });

  const unlinkMutation = useSupabaseMutation<void, string>({
    mutationFn: unlinkActionTask,
    invalidateKeys: defectActionKeys.links(defectId),
    successMessage: t("defects.measures.toasts.unlinkSuccess"),
    errorMessage: t("defects.measures.toasts.unlinkError"),
    showErrorDetails: true,
    toastType: "sonner",
  });

  return {
    actions,
    linksByAction,
    progress,
    canClose,
    isLoading: actionsQuery.isLoading,
    error: actionsQuery.error as Error | null,
    addAction: addMutation.mutate,
    setActionStatus: statusMutation.mutate,
    removeAction: removeMutation.mutate,
    applyRules: () => applyRulesMutation.mutate(),
    linkActionTask: linkMutation.mutate,
    unlinkActionTask: unlinkMutation.mutate,
    isMutating:
      addMutation.isPending ||
      statusMutation.isPending ||
      removeMutation.isPending ||
      applyRulesMutation.isPending ||
      linkMutation.isPending ||
      unlinkMutation.isPending,
  };
}
